'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  useMeQuery,
  useUpdateFirstLoginAtMutation,
} from '@frameio/graphql-client/generated';
import { useTrackUserFirstLogin } from '../../segment/hooks/useTrackUserFirstLogin';

type FirstLoginContextType = {
  isFirstLogin: boolean;
  isFirstLoginLoading: boolean;
};

export const FirstLoginContext = createContext<FirstLoginContextType | null>(
  null
);

export const useFirstLoginContext = () => {
  const context = useContext(FirstLoginContext);
  if (!context) {
    throw new Error(
      'useFirstLoginContext must be used within FirstLoginProvider'
    );
  }
  return context;
};

/**
 * Provider that determines first-login state and runs one-time side-effects.
 *
 * - Reads the authenticated user via `useMeQuery`.
 * - If `me.firstLoginAt` is unset for an authenticated user, it:
 *   - Calls `updateFirstLoginAt` to persist the first-login timestamp
 *   - Emits analytics via `useTrackUserFirstLogin`
 *   - Ensures `isFirstLogin` stays true for the remainder of the  current session.
 *
 */
export const FirstLoginProvider = ({ children }: PropsWithChildren) => {
  const { data, loading } = useMeQuery({ returnPartialData: true });
  const [updateFirstLoginAt] = useUpdateFirstLoginAtMutation();
  const trackFirstLogin = useTrackUserFirstLogin(data?.me);
  // We need to preserve this state for the session to avoid the
  // GQL cache update that will overwrite the firstLoginAt field
  const hasProcessedFirstLogin = useRef<boolean>(false);
  const wasFirstLoginThisSession = useRef<boolean>(false);

  useEffect(() => {
    if (loading || !data?.me || data.me.__typename !== 'AuthenticatedUser') {
      return;
    }

    const isFirstLogin = typeof data.me.firstLoginAt !== 'string';

    if (isFirstLogin && !hasProcessedFirstLogin.current) {
      hasProcessedFirstLogin.current = true;
      wasFirstLoginThisSession.current = true;
      updateFirstLoginAt();
      trackFirstLogin();
    }
  }, [data?.me, loading, updateFirstLoginAt, trackFirstLogin]);

  const value = useMemo(
    () => ({
      isFirstLogin:
        data?.me?.__typename === 'AuthenticatedUser' &&
        (typeof data.me.firstLoginAt !== 'string' ||
          wasFirstLoginThisSession.current),
      isFirstLoginLoading: loading,
    }),
    [data?.me, loading]
  );

  return (
    <FirstLoginContext.Provider value={value}>
      {children}
    </FirstLoginContext.Provider>
  );
};
